import { useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import {
  useAcceptChatRequestMutation,
  useGetChatRoomsTotalChatRequestQuery,
} from 'store/api/yay/chat'
import { setSelectedChatRoomId } from 'store/slices/chat'

import useChatRoomLogic from 'features/Chat/hocs/useChatRoomLogic'
import usePageModal from 'features/Modal/hooks/usePageModal'
import {
  snowplowEvent,
  userToUserContextSchemaData,
  chatRoomToChatRoomContextSchema,
} from 'lib/analytics'
import { ChatRoomType } from 'lib/_types'

import Button from 'components/Button'
import { MODAL_MODE } from 'components/Modal/config'
import { useReportModal } from 'components/Modal/ReportModal/ReportModal'
import { ROUTE_PATH } from 'lib/paths'

const RequestToChatOptionPanel = ({
  chatRoom,
  opponentUser,
}: {
  chatRoom?: ChatRoomType
  opponentUser: {
    id: number
    nickname: string
  }
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const reportModal = useReportModal()
  const { generatePageModalUrl } = usePageModal()

  const { onDeleteChat, onDeleteChatIsLoading } = useChatRoomLogic({
    chatRoomId: chatRoom.id,
  })

  const getChatRoomsTotalChatRequestRes = useGetChatRoomsTotalChatRequestQuery()

  const [acceptChatRequest, acceptChatRequestRes] =
    useAcceptChatRequestMutation()

  useEffect(() => {
    if (acceptChatRequestRes.isSuccess && chatRoom) {
      dispatch(setSelectedChatRoomId(chatRoom?.id))
      navigate(ROUTE_PATH.CHAT)
    }
  }, [acceptChatRequestRes])

  return (
    <div className="RequestToChatOptionPanel">
      <div className="RequestToChatOptionPanel__title">
        {t('request_to_chat_option_panel.title')}
      </div>

      <div className="RequestToChatOptionPanel__button_wrapper">
        <Button
          buttonStyle="bordered-red"
          disabled={onDeleteChatIsLoading}
          onClick={() => {
            onDeleteChat({
              redirectUrl:
                getChatRoomsTotalChatRequestRes?.data?.total > 1
                  ? generatePageModalUrl(MODAL_MODE.REQUEST_CHAT_LIST)
                  : ROUTE_PATH.CHAT,
              onSuccess: () => {
                const user = chatRoom.members.find(
                  (m) => m.id === opponentUser.id
                )

                snowplowEvent.delete_chat({
                  user: userToUserContextSchemaData({ user }),
                  chatRoom: chatRoomToChatRoomContextSchema({ chatRoom }),
                })
              },
            })
          }}
        >
          {t('common.delete')}
        </Button>

        <Button
          buttonStyle="bordered-green"
          loading={acceptChatRequestRes.isLoading}
          onClick={() => {
            acceptChatRequest({
              chatRoomIds: [chatRoom?.id],
            })
          }}
        >
          {t('request_to_chat_option_panel.button.accept')}
        </Button>
      </div>

      <a
        className="RequestToChatOptionPanel__report_user"
        onClick={() =>
          reportModal.open({
            userId: opponentUser?.id?.toString?.(),
          })
        }
      >
        {t('request_to_chat_option_panel.report_user', {
          nickname: opponentUser?.nickname,
        })}
      </a>
    </div>
  )
}

RequestToChatOptionPanel.propTypes = {}

export default RequestToChatOptionPanel
